import React from 'react';
import { ExternalLink, FileStack, Combine, Scissors, Minimize2, FileSignature, ShieldCheck } from 'lucide-react';
import { PORTAL_SHELL } from '../portalTheme';
import { getStirlingPdfUrl } from '../../../constants';

/** Typische PDF-Aufgaben (nur Beschreibung — Link führt jeweils auf die Stirling-Startseite). */
const PDF_HIGHLIGHTS: Array<{ icon: typeof Combine; label: string; hint: string }> = [
  { icon: Combine, label: 'Zusammenführen', hint: 'Mehrere PDFs zu einem Dokument verbinden.' },
  { icon: Scissors, label: 'Teilen', hint: 'Seiten herauslösen oder Dokument aufteilen.' },
  { icon: Minimize2, label: 'Komprimieren', hint: 'Dateigröße für E-Mail/Upload reduzieren.' },
  { icon: FileSignature, label: 'Signieren', hint: 'Unterschrift oder Stempel einfügen.' },
];

/**
 * PDF-Werkzeuge (Stirling-PDF) — zusätzlich zu OnlyOffice (DOCX/Office).
 * Öffnet Stirling in einem neuen Tab (kein iframe → keine CSP-frame-src-Erweiterung,
 * keine Konflikte mit Stirlings eigenen Anti-Clickjacking-Headern).
 */
export const PdfToolsView: React.FC = () => {
  const baseUrl = getStirlingPdfUrl();
  const available = Boolean(baseUrl);

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl mx-auto">
      <header>
        <h1 className="text-xl font-semibold text-white tracking-tight">PDF-Werkzeuge</h1>
        <p className={`${PORTAL_SHELL.textMuted} text-sm mt-1`}>
          Schneller PDF-Werkzeugkasten — zusammenführen, teilen, komprimieren, signieren und mehr.
          Self-Hosted im ARKORBIT-Labor, kein Datenversand an Dritte.
        </p>
      </header>

      {available ? (
        <section className={`${PORTAL_SHELL.ingestBar} p-5 space-y-4`}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-sky-500/10 border border-sky-500/20 flex items-center justify-center flex-shrink-0">
              <FileStack className="w-5 h-5 text-sky-400" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-white text-sm font-medium">PDF-Werkzeuge öffnen</h2>
              <p className="text-slate-400 text-sm mt-1 leading-relaxed">
                Öffnet den PDF-Werkzeugkasten in einem neuen Tab. Office-Dateien (Word, …) bleiben
                weiterhin im OnlyOffice-Editor unter „Dokumente“.
              </p>
            </div>
          </div>
          <a
            href={baseUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4" aria-hidden />
            PDF-Werkzeuge öffnen
          </a>
          <div className="flex items-start gap-2 text-xs text-slate-500 leading-relaxed">
            <ShieldCheck className="w-4 h-4 text-emerald-500/80 flex-shrink-0 mt-0.5" aria-hidden />
            Self-Hosted · EU · zusätzlich zu OnlyOffice
          </div>
        </section>
      ) : (
        <section className={`${PORTAL_SHELL.ingestBar} p-5 space-y-3`}>
          <p className="text-slate-400 text-sm leading-relaxed">
            Die PDF-Werkzeuge sind für diese Umgebung noch nicht freigeschaltet. Auf den
            Labor-/Demo-Portalen stehen sie automatisch unter <code>pdf-labor.arkorbit.at</code> bereit.
          </p>
        </section>
      )}

      <section className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Typische Aufgaben</h2>
        <div className="grid gap-2 text-sm text-slate-400 sm:grid-cols-2">
          {PDF_HIGHLIGHTS.map(({ icon: Icon, label, hint }) => (
            <p key={label} className="flex items-start gap-2">
              <Icon className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" aria-hidden />
              <span>
                <span className="text-slate-300 font-medium">{label}</span> — {hint}
              </span>
            </p>
          ))}
        </div>
      </section>
    </div>
  );
};
